// (c) TwistedMinds, Gerry Uytdenhouwen - 28 oktober 2026

/**
 * Cart line handling for the order widget.
 *
 * Every product that is added to the cart becomes its own line, even when the
 * same product (with or without the same variation) is already in the cart:
 * the shop packs every line separately. A line is identified by its `line_id`,
 * never by `id_product` or `variation_name`.
 *
 * The functions change the given cart array in place with push/splice, so the
 * array stays reactive when it is a Vue data property.
 */

let lineCounter = 0;

/**
 * Generates a unique client-side id for a cart line
 * @return {string}
 */
export function newLineId() {
    lineCounter++;
    return 'l' + Date.now().toString(36) + '-' + lineCounter + '-' + Math.random().toString(36).slice(2, 8);
}

/**
 * Builds a new cart line for a product
 * @param product
 * @param qty
 * @param variationName
 * @param variationPrice
 * @return {object}
 */
export function buildCartLine(product, qty, variationName = '', variationPrice = '') {
    return {
        'line_id': newLineId(),
        'id_product': product.id,
        'name': product.name,
        'image': product.image,
        'price': product.price,
        'categories': product.categories,
        'variation_price': variationPrice,
        'variation_name': variationName || '',
        'qty_product': Number(qty),
        'ingredients': product.ingredients,
    };
}

/**
 * Adds a line to the cart, always as a new separate line
 * @param cart
 * @param line
 * @return {object} the added line
 */
export function addCartLine(cart, line) {
    if (!line.line_id) {
        line.line_id = newLineId();
    }
    cart.push(line);
    return line;
}

/**
 * @param cart
 * @param lineId
 * @return {number}
 */
export function findLineIndex(cart, lineId) {
    return cart.findIndex(line => line.line_id === lineId);
}

/**
 * Removes exactly one line from the cart
 * @param cart
 * @param lineId
 * @return {boolean} true when a line was removed
 */
export function removeCartLine(cart, lineId) {
    const index = findLineIndex(cart, lineId);
    if (index === -1) {
        return false;
    }
    cart.splice(index, 1);
    return true;
}

/**
 * Sets the quantity of one line; a quantity below 1 removes that line
 * @param cart
 * @param lineId
 * @param qty
 * @return {object|null} the updated line, or null when it was removed or not found
 */
export function setLineQty(cart, lineId, qty) {
    const index = findLineIndex(cart, lineId);
    if (index === -1) {
        return null;
    }
    const quantity = parseInt(qty, 10);
    if (!(quantity > 0)) {
        cart.splice(index, 1);
        return null;
    }
    cart[index].qty_product = quantity;
    return cart[index];
}

/**
 * Raises or lowers the quantity of one line by `delta`
 * @param cart
 * @param lineId
 * @param delta
 * @return {object|null}
 */
export function changeLineQty(cart, lineId, delta) {
    const index = findLineIndex(cart, lineId);
    if (index === -1) {
        return null;
    }
    return setLineQty(cart, lineId, (parseInt(cart[index].qty_product, 10) || 0) + delta);
}

/**
 * Price of one line: (product price + variation price) * quantity
 * @param line
 * @return {number}
 */
export function lineTotal(line) {
    const price = parseFloat(line.price) || 0;
    const variationPrice = parseFloat(line.variation_price) || 0;
    const qty = parseInt(line.qty_product, 10) || 0;
    return (price + variationPrice) * qty;
}

/**
 * Total of all cart lines
 * @param cart
 * @return {number}
 */
export function cartTotal(cart) {
    return cart.reduce((sum, line) => sum + lineTotal(line), 0);
}

/**
 * Makes a cart from an older session usable: every line gets a unique
 * `line_id` and an empty variation is always an empty string
 * @param lines
 * @return {object[]}
 */
export function normaliseCart(lines) {
    if (!Array.isArray(lines)) {
        return [];
    }
    const seen = new Set();
    return lines
        .filter(line => line && typeof line === 'object')
        .map(line => {
            const normalised = Object.assign({}, line);
            if (!normalised.line_id || seen.has(normalised.line_id)) {
                normalised.line_id = newLineId();
            }
            seen.add(normalised.line_id);
            if (normalised.variation_name === null || normalised.variation_name === undefined) {
                normalised.variation_name = '';
            }
            if (normalised.variation_price === null || normalised.variation_price === undefined) {
                normalised.variation_price = '';
            }
            return normalised;
        });
}

/**
 * Parses a product field that holds JSON (variation_list / variation_title)
 * @param value
 * @return {Array}
 */
function parseJsonList(value) {
    if (Array.isArray(value)) {
        return value;
    }
    if (!value) {
        return [];
    }
    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        return [];
    }
}

/**
 * Variation groups of a product with the value that a cart line currently has
 * per group. Returns an empty array when the product has no variations.
 *
 * The line's `variation_name` holds the chosen values joined with ',' in the
 * order of the product's variation groups, exactly as the product popup
 * stores them.
 * @param product
 * @param line
 * @return {{id: *, title: string, options: {value: string, price: *}[], selected: string}[]}
 */
export function lineVariationGroups(product, line) {
    if (!product) {
        return [];
    }
    const list = parseJsonList(product.variation_list).filter(item => item && item.value);
    if (list.length === 0) {
        return [];
    }
    const names = String((line && line.variation_name) || '').split(',');
    return parseJsonList(product.variation_title)
        .filter(group => group)
        .map((group, index) => {
            const options = list
                .filter(item => item.variation_id === group.id)
                .map(item => ({value: item.value, price: item.price}));
            const values = options.map(option => option.value);
            let selected = values.includes(names[index]) ? names[index] : (names.find(name => values.includes(name)) || '');
            return {id: group.id, title: group.title, options, selected};
        })
        .filter(group => group.options.length > 0);
}

/**
 * Changes the variation of exactly one cart line. `groupIndex` is the index in
 * the list from lineVariationGroups(); the line gets the same
 * `variation_name` (values joined with ',') and `variation_price` (sum of the
 * chosen variation prices) as an addition through the product popup.
 * @param cart
 * @param lineId
 * @param product
 * @param groupIndex
 * @param value
 * @return {object|null} the updated line, or null when nothing changed
 */
export function setLineVariation(cart, lineId, product, groupIndex, value) {
    const index = findLineIndex(cart, lineId);
    if (index === -1) {
        return null;
    }
    const line = cart[index];
    const groups = lineVariationGroups(product, line);
    const group = groups[groupIndex];
    if (!group || !group.options.some(option => option.value === value)) {
        return null;
    }
    group.selected = value;
    const chosen = groups
        .map(item => item.options.find(option => option.value === item.selected))
        .filter(option => option && option.value && String(option.value).trim().length > 0);
    line.variation_name = chosen.map(option => option.value).join(',');
    line.variation_price = chosen.map(option => parseFloat(option.price) || 0).reduce((sum, price) => sum + price, 0);
    return line;
}
